import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useSearchParams } from 'react-router-dom';
import { SearchX } from 'lucide-react';
import Filter, {
  applyFilters,
  facetCounts,
  sortItems,
  parseFilterQuery,
  buildFilterQuery,
} from '@ajat/design-system/Filter';
import ProductsGrid from '@ajat/design-system/ContentBlocks/ProductsGrid';
import EmptyState from '@ajat/design-system/EmptyState';
import { ComponentPage, DemoCard, ViewportFrame } from '../shared';
import { LISTING_FRAME_MARK } from './content/contentDemoData';
import {
  FILTER_ATTRIBUTES,
  EXTRA_ATTRIBUTES,
  SORT_OPTIONS,
  SORTERS,
  FILTER_ITEMS,
  toCard,
} from './filterDemoData';

export const meta = {
  component: Filter,
  title: 'Filter',
  group: 'Commerce',
  importPath: '@ajat/design-system/Filter',
  figmaNode: '4411:18956',
  description:
    'The toolbar above a product listing — filter attributes on the left, sort on the right. Desktop: a trigger per attribute opens a panel in the Input dropdown look (check boxes, a search field over long lists, or a price range) and every choice applies at once. Mobile: "Filters" opens a sheet with every attribute — a draft until "Show N products". Sort is a panel on desktop and a sheet of radio buttons on mobile. Active choices show as counts on the triggers, plus removable chips under the bar on desktop (phones: just the count on "Filters" — clearing is in the sheet).',
  props: [
    ['attributes', 'array', '—', "Plain data, in bar order: { id, label, type: 'options', options[{ value, label, swatch?, count?, disabled? }], multiple?, searchable?, field?, param? } or { id, label, type: 'range', min, max, step?, unit?, field?, param? }. field = the item key applyFilters reads (default id); param = the URL name (default id). Lists with more than 8 options get a search field unless searchable says otherwise"],
    ['value', 'object', '{}', 'Applied choices: { [attributeId]: string[] | [min, max] } — normalised on the way in (unknown values, empty lists and full-width ranges dropped)'],
    ['onChange', 'func', '—', 'Receives the next value — on desktop for every choice, on mobile when "Show N products" is pressed'],
    ['sortOptions', 'array', '[]', 'Sort choices { value, label }; none → no sort trigger'],
    ['sort', 'string', 'null', 'The current sort value'],
    ['onSortChange', 'func', '—', 'Receives the picked sort value (a pick closes the panel / sheet)'],
    ['resultCount', 'number', '—', 'Results for the applied value, read out by the polite live region; defaults to countResults(value)'],
    ['countResults', 'func', '—', '(value) => number — the drawer\'s "Show N products" follows the draft through it; 0 disables the button'],
    ['getCounts', 'func', '—', '(value) => { [attributeId]: { [optionValue]: count } } — per-option counts (facetCounts); an option at 0 is disabled unless picked'],
    ['maxInline', 'number', '4', 'Desktop triggers before the rest fold into "All filters" (the drawer as a side panel)'],
    ['sticky', 'bool', 'false', 'Stick under the header (--header-height) and slide away scrolling down, back scrolling up — never while a panel is open'],
    ['showChips', 'bool', 'true', 'Removable chips for the active choices under the bar, with "Clear all" — desktop only; phones show just the count on "Filters" and clear in the drawer'],
    ['formatValue', 'func', '—', '(number, attribute) => string for range values; default "200 kr" from attribute.unit'],
    ['labels', 'object', 'English', 'Copy overrides — filters, allFilters, sort, clear, clearAll, close, search, noMatches, showAll(n), showLess, from, to, rangeMin(label), rangeMax(label), apply, showResults(n), noResults, results(n), activeFilters, removeChip(label)'],
    ['className', 'string', "''", 'Additional CSS class on the root'],
  ],
  propsNote:
    'Controlled and router-agnostic. The same entry exports the pure helpers a listing page needs: applyFilters / facetCounts / sortItems (options OR within an attribute, AND across attributes) and parseFilterQuery / buildFilterQuery for the URL (?color=black,red&price=200-600&sort=price-asc — write it with replace). Skin: --filter-bar-* / --filter-divider-* / --filter-panel-* in component-tokens.css; the panels reuse the Input dropdown tokens. The trigger labels are gray-10 as in Figma Tertiary/Text (3617:1099) — the DS text variant is gray-5.',
  usage: [
    {
      title: 'A listing page with the state in the URL',
      code: `import Filter, { applyFilters, facetCounts, sortItems, parseFilterQuery, buildFilterQuery }
  from '@ajat/design-system/Filter';

const [params, setParams] = useSearchParams();
const { value, sort } = parseFilterQuery(params, ATTRIBUTES, { sortOptions: SORT_OPTIONS });
const write = (next) =>
  setParams(buildFilterQuery(next, ATTRIBUTES, { search: params, defaultSort: 'recommended' }), { replace: true });

const items = sortItems(applyFilters(PRODUCTS, ATTRIBUTES, value), sort, SORTERS);

<Filter
  sticky
  attributes={ATTRIBUTES}
  value={value}
  onChange={(v) => write({ value: v, sort })}
  sortOptions={SORT_OPTIONS}
  sort={sort || 'recommended'}
  onSortChange={(s) => write({ value, sort: s })}
  countResults={(v) => applyFilters(PRODUCTS, ATTRIBUTES, v).length}
  getCounts={(v) => facetCounts(PRODUCTS, ATTRIBUTES, v)}
  labels={{ filters: 'Filter', sort: 'Sortera', clearAll: 'Rensa alla',
            showResults: (n) => \`Visa \${n} produkter\`, results: (n) => \`\${n} produkter\` }}
/>
<ProductsGrid items={items} />`,
    },
    {
      title: 'Attributes',
      code: `const ATTRIBUTES = [
  { id: 'color', label: 'Color', type: 'options', options: [
    { value: 'black', label: 'Black', swatch: '#1b1b1b' },
    { value: 'red', label: 'Red', swatch: '#b3262e' },
  ] },
  { id: 'school', label: 'School', type: 'options', options: SCHOOLS },   // > 8 → search field
  { id: 'fit', label: 'Fit', type: 'options', multiple: false, options: FITS },
  { id: 'price', label: 'Price', type: 'range', min: 0, max: 1000, step: 10, unit: 'kr', field: 'priceValue' },
];`,
    },
  ],
};

// `require()` of an SVG returns the module (svgr) — the card wants the URL
const FRAME_MARK = LISTING_FRAME_MARK?.default || LISTING_FRAME_MARK;

const countFor = (attributes) => (v) => applyFilters(FILTER_ITEMS, attributes, v).length;
const countsFor = (attributes) => (v) => facetCounts(FILTER_ITEMS, attributes, v);

/** The full pattern: URL state → Filter → filtered + sorted ProductsGrid */
function LiveListing({ brand }) {
  const [params, setParams] = useSearchParams();
  const { value, sort } = useMemo(
    () => parseFilterQuery(params, FILTER_ATTRIBUTES, { sortOptions: SORT_OPTIONS }),
    [params],
  );
  const write = (next) => setParams(
    buildFilterQuery(next, FILTER_ATTRIBUTES, { search: params, defaultSort: 'recommended' }),
    { replace: true },
  );
  const items = useMemo(
    () => sortItems(applyFilters(FILTER_ITEMS, FILTER_ATTRIBUTES, value), sort, SORTERS),
    [value, sort],
  );
  const query = buildFilterQuery({ value, sort }, FILTER_ATTRIBUTES, { defaultSort: 'recommended' });

  return (
    <div>
      <div style={{ paddingTop: 'var(--space-ui-medium-l)' }}>
        <Filter
          attributes={FILTER_ATTRIBUTES}
          value={value}
          onChange={(v) => write({ value: v, sort })}
          sortOptions={SORT_OPTIONS}
          sort={sort || 'recommended'}
          onSortChange={(s) => write({ value, sort: s })}
          countResults={countFor(FILTER_ATTRIBUTES)}
          getCounts={countsFor(FILTER_ATTRIBUTES)}
        />
      </div>
      {items.length > 0 ? (
        <ProductsGrid
          brand={brand}
          align="left"
          desktopColumns={3}
          paddingTop="medium-l"
          items={items.map(toCard)}
          cardProps={{ frameMark: FRAME_MARK }}
        />
      ) : (
        <div style={{ padding: 'var(--space-ui-large-m) var(--space-ui-medium-l)' }}>
          <EmptyState
            icon={SearchX}
            size="drawer"
            title="No products match"
            message="Try fewer filters, or clear them all."
            actions={[{ label: 'Clear filters', variant: 'outline', onClick: () => write({ value: {}, sort }) }]}
          />
        </div>
      )}
      <p className="typo-body-sm" style={{ padding: '0 var(--space-ui-medium-l) var(--space-ui-medium-l)', color: 'var(--color-gray-6)' }}>
        URL query: {query ? `?${query}` : '— (nothing chosen)'}
      </p>
    </div>
  );
}

LiveListing.propTypes = { brand: PropTypes.string };

/** A local-state Filter for the static demos */
function StatefulFilter({ attributes = FILTER_ATTRIBUTES, initial = {}, ...props }) {
  const [value, setValue] = useState(initial);
  const [sort, setSort] = useState('recommended');
  return (
    <Filter
      attributes={attributes}
      value={value}
      onChange={setValue}
      sortOptions={SORT_OPTIONS}
      sort={sort}
      onSortChange={setSort}
      countResults={countFor(attributes)}
      getCounts={countsFor(attributes)}
      {...props}
    />
  );
}

StatefulFilter.propTypes = {
  attributes: PropTypes.array,
  initial: PropTypes.object,
};

const demoPad = { padding: 'var(--space-ui-medium-l) 0', minHeight: 420 };

export default function FilterPage({ brand = 'CLS' }) {
  return (
    <ComponentPage meta={meta}>
      <DemoCard title="Live listing" hint="choices filter the grid and write the URL query" flush>
        <LiveListing brand={brand} />
      </DemoCard>

      <DemoCard title="Active filters" hint="counts on the triggers, chips under the bar (desktop)" flush>
        <div style={demoPad}>
          <StatefulFilter initial={{ color: ['red', 'black'], collection: ['edition'], price: [300, 700] }} />
        </div>
      </DemoCard>

      <DemoCard title="All filters" hint="six attributes, maxInline 4 — the rest fold into the side panel" flush>
        <div style={demoPad}>
          <StatefulFilter attributes={[...FILTER_ATTRIBUTES, ...EXTRA_ATTRIBUTES]} initial={{ fit: ['oversize'] }} />
        </div>
      </DemoCard>

      <DemoCard title="Mobile" hint="402px — only Filters + count and Sort by; both open bottom sheets, no chip row" flush>
        <ViewportFrame width={402} height={760} label="Filter at 402px">
          <div style={{ padding: 'var(--space-ui-medium-l) 0' }}>
            <StatefulFilter initial={{ color: ['white'] }} />
          </div>
        </ViewportFrame>
      </DemoCard>

      <DemoCard title="Without sort or chips" flush>
        <div style={demoPad}>
          <StatefulFilter sortOptions={[]} showChips={false} initial={{ collection: ['classic'] }} />
        </div>
      </DemoCard>
    </ComponentPage>
  );
}

FilterPage.propTypes = { brand: PropTypes.string };
